/**
 * Single source of truth for npm packages allowed in the DayOne sandbox.
 *
 * CRITICAL: When adding packages here, also update the system prompts in:
 * - supabase/functions/stream-app-build/index.ts (3 locations: ~line 930, 947, 1534)
 * - supabase/functions/builder-chat-openai/index.ts (~line 2159)
 * - supabase/functions/generate-app-context/index.ts (~line 322)
 *
 * The prompts can't import this file (they're Deno edge functions with embedded strings),
 * so they must be manually kept in sync. Each prompt location has a comment pointing here.
 */

/**
 * Core packages that the AI should know about and actively use.
 * These appear in system prompts.
 */
export const ALLOWED_NPM_PACKAGES = [
  // Core React ecosystem
  "react",
  "react-dom",
  "react-router-dom",

  // Animation & Icons
  "framer-motion",
  "lucide-react",

  // Utilities
  "clsx",
  "tailwind-merge",

  // Auth & Payments
  "@supabase/supabase-js",
  "@stripe/stripe-js",

  // State management
  "zustand",

  // Charts
  "recharts",

  // Export tools
  "jspdf",
  "html2canvas",
  "xlsx",
  "papaparse",
  "react-to-print",
] as const;

/**
 * Additional packages allowed in sandbox but NOT promoted in prompts.
 * These are peer dependencies or legacy packages we support but don't advertise.
 */
export const SANDBOX_ONLY_PACKAGES = [
  // Peer dependencies of react-router-dom
  "react-router",
  "@remix-run/router",

  // Additional state management (allowed but not promoted)
  "immer",
  "jotai",
  "valtio",

  // Date utilities (allowed but prompts say "use native Date")
  "date-fns",
] as const;

/**
 * Combined list for sandbox runtime validation.
 */
export const ALL_SANDBOX_ALLOWED_PACKAGES = [
  ...ALLOWED_NPM_PACKAGES,
  ...SANDBOX_ONLY_PACKAGES,
] as const;

/**
 * Extract base package name from specifier.
 * Handles scoped packages (@scope/pkg) and subpaths (pkg/subpath).
 */
export function getPackageName(spec: string): string {
  const s = spec.trim();
  if (s.startsWith("@")) {
    const parts = s.split("/");
    return parts.length >= 2 ? `${parts[0]}/${parts[1]}` : s;
  }
  return s.split("/")[0] ?? s;
}

/**
 * Check if a package specifier is allowed in the sandbox.
 */
export function isAllowedPackage(spec: string): boolean {
  const pkg = getPackageName(spec);
  return (ALL_SANDBOX_ALLOWED_PACKAGES as readonly string[]).some(
    (allowed) => allowed === pkg || spec === allowed || spec.startsWith(allowed + "/"),
  );
}

/**
 * Packages for system prompt text (human-readable, without peer deps).
 * Use this when generating prompt text that lists allowed packages.
 */
export const PROMPT_PACKAGE_LIST = ALLOWED_NPM_PACKAGES.filter(
  (p) => !["react", "react-dom"].includes(p) // These are implicit
).join(", ");
